// Service management utilities
// Handles service CRUD operations directly via Supabase client
// Created for service/department classification of missions
// Services are automatically sorted alphabetically by name
// CHANGE: Removed all debug console statements for production

import { supabase } from './supabase';

export interface Service {
  id: string;
  name: string;
  color: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ServiceResponse {
  success: boolean;
  error?: string;
  data?: Service;
}

export interface ServicesListResponse {
  success: boolean;
  error?: string;
  data?: Service[];
}

// Get all services (active by default)
export async function getServices(includeArchived = false): Promise<ServicesListResponse> {
  try {
    let query = supabase
      .from('services')
      .select('*')
      .order('name', { ascending: true });

    if (!includeArchived) {
      query = query.eq('is_active', true);
    }

    const { data, error } = await query;

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true, data: data || [] };
  } catch (error) {
    return { success: false, error: 'Une erreur est survenue' };
  }
}

// Get single service by ID
export async function getServiceById(serviceId: string): Promise<ServiceResponse> {
  try {
    const { data, error } = await supabase
      .from('services')
      .select('*')
      .eq('id', serviceId)
      .maybeSingle();

    if (error) {
      return { success: false, error: error.message };
    }

    if (!data) {
      return { success: false, error: 'Service non trouvé' };
    }

    return { success: true, data };
  } catch (error) {
    return { success: false, error: 'Une erreur est survenue' };
  }
}

// Create a new service (Manager/Director only)
export async function createService(
  name: string,
  color: string
): Promise<ServiceResponse> {
  try {
    const { data, error } = await supabase
      .from('services')
      .insert({
        name,
        color,
        is_active: true
      })
      .select()
      .single();

    if (error) {
      // Handle unique constraint violation
      if (error.code === '23505') {
        return { success: false, error: 'Un service avec ce nom existe déjà' };
      }

      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    return { success: false, error: 'Une erreur est survenue' };
  }
}

// Update a service (Manager/Director only)
export async function updateService(
  serviceId: string,
  updates: {
    name?: string;
    color?: string;
  }
): Promise<ServiceResponse> {
  try {
    const { data, error } = await supabase
      .from('services')
      .update(updates)
      .eq('id', serviceId)
      .select()
      .single();

    if (error) {
      // Handle unique constraint violation
      if (error.code === '23505') {
        return { success: false, error: 'Un service avec ce nom existe déjà' };
      }

      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    return { success: false, error: 'Une erreur est survenue' };
  }
}

// Archive a service (Manager/Director only)
export async function archiveService(serviceId: string): Promise<ServiceResponse> {
  try {
    // Check if any active task templates use this service
    const { data: templates, error: checkError } = await supabase
      .from('task_templates')
      .select('id')
      .eq('service_id', serviceId)
      .limit(1);

    if (checkError) {
      return { success: false, error: checkError.message };
    }

    if (templates && templates.length > 0) {
      return {
        success: false,
        error: 'Impossible d\'archiver ce service car des missions l\'utilisent'
      };
    }

    const { data, error } = await supabase
      .from('services')
      .update({ is_active: false })
      .eq('id', serviceId)
      .select()
      .single();

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    return { success: false, error: 'Une erreur est survenue' };
  }
}

// Restore an archived service (Manager/Director only)
export async function restoreService(serviceId: string): Promise<ServiceResponse> {
  try {
    const { data, error } = await supabase
      .from('services')
      .update({ is_active: true })
      .eq('id', serviceId)
      .select()
      .single();

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    return { success: false, error: 'Une erreur est survenue' };
  }
}

// Get count of task templates using a service
export async function getServiceTemplateCount(serviceId: string): Promise<number> {
  try {
    const { count, error } = await supabase
      .from('task_templates')
      .select('id', { count: 'exact', head: true })
      .eq('service_id', serviceId);

    if (error) {
      return 0;
    }

    return count || 0;
  } catch (error) {
    return 0;
  }
}

// Available color palette for services
export const SERVICE_COLORS = [
  { value: 'blue', label: 'Bleu', class: 'bg-blue-100 text-blue-800 border-blue-200' },
  { value: 'cyan', label: 'Cyan', class: 'bg-cyan-100 text-cyan-800 border-cyan-200' },
  { value: 'teal', label: 'Teal', class: 'bg-teal-100 text-teal-800 border-teal-200' },
  { value: 'lime', label: 'Lime', class: 'bg-lime-100 text-lime-800 border-lime-200' },
  { value: 'orange', label: 'Orange', class: 'bg-orange-100 text-orange-800 border-orange-200' },
  { value: 'amber', label: 'Ambre', class: 'bg-amber-100 text-amber-800 border-amber-200' },
  { value: 'rose', label: 'Rose', class: 'bg-rose-100 text-rose-800 border-rose-200' },
  { value: 'pink', label: 'Rose vif', class: 'bg-pink-100 text-pink-800 border-pink-200' },
  { value: 'violet', label: 'Violet', class: 'bg-violet-100 text-violet-800 border-violet-200' },
  { value: 'fuchsia', label: 'Fuchsia', class: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200' },
  { value: 'sky', label: 'Ciel', class: 'bg-sky-100 text-sky-800 border-sky-200' },
  { value: 'indigo', label: 'Indigo', class: 'bg-indigo-100 text-indigo-800 border-indigo-200' }
];

// Get Tailwind class for service color badge
export function getServiceColorClass(color: string): string {
  const colorConfig = SERVICE_COLORS.find(c => c.value === color);
  return colorConfig?.class || 'bg-gray-100 text-gray-800 border-gray-200';
}

// Get small dot color class for task blocks
export function getServiceDotColorClass(color: string): string {
  const dotColors: Record<string, string> = {
    blue: 'bg-blue-500',
    cyan: 'bg-cyan-500',
    teal: 'bg-teal-500',
    lime: 'bg-lime-500',
    orange: 'bg-orange-500',
    amber: 'bg-amber-500',
    rose: 'bg-rose-500',
    pink: 'bg-pink-500',
    violet: 'bg-violet-500',
    fuchsia: 'bg-fuchsia-500',
    sky: 'bg-sky-500',
    indigo: 'bg-indigo-500'
  };

  return dotColors[color] || 'bg-gray-500';
}

// Get service background color class for task blocks (Team View)
// Returns pastel background colors for elegant display
export function getServiceBackgroundClass(color: string): string {
  const backgroundColors: Record<string, string> = {
    blue: 'bg-blue-100',
    cyan: 'bg-cyan-100',
    teal: 'bg-teal-100',
    lime: 'bg-lime-100',
    orange: 'bg-orange-100',
    amber: 'bg-amber-100',
    rose: 'bg-rose-100',
    pink: 'bg-pink-100',
    violet: 'bg-violet-100',
    fuchsia: 'bg-fuchsia-100',
    sky: 'bg-sky-100',
    indigo: 'bg-indigo-100'
  };

  return backgroundColors[color] || 'bg-gray-100';
}

// Get service border color class for task blocks (Team View)
// Returns medium-tone borders for elegant display with pastel backgrounds
export function getServiceBorderClass(color: string): string {
  const borderColors: Record<string, string> = {
    blue: 'border-blue-300',
    cyan: 'border-cyan-300',
    teal: 'border-teal-300',
    lime: 'border-lime-300',
    orange: 'border-orange-300',
    amber: 'border-amber-300',
    rose: 'border-rose-300',
    pink: 'border-pink-300',
    violet: 'border-violet-300',
    fuchsia: 'border-fuchsia-300',
    sky: 'border-sky-300',
    indigo: 'border-indigo-300'
  };

  return borderColors[color] || 'border-gray-300';
}

// Get service text color class
// Returns dark text colors for readability on pastel backgrounds
export function getServiceTextClass(color: string): string {
  const textColors: Record<string, string> = {
    blue: 'text-blue-800',
    cyan: 'text-cyan-800',
    teal: 'text-teal-800',
    lime: 'text-lime-800',
    orange: 'text-orange-800',
    amber: 'text-amber-800',
    rose: 'text-rose-800',
    pink: 'text-pink-800',
    violet: 'text-violet-800',
    fuchsia: 'text-fuchsia-800',
    sky: 'text-sky-800',
    indigo: 'text-indigo-800'
  };

  return textColors[color] || 'text-gray-800';
}

// Get service accent hex color for inline styles (e.g. left accent bar in list rows)
export function getServiceAccentHex(color: string): string {
  const hexColors: Record<string, string> = {
    blue:    '#3b82f6',
    cyan:    '#06b6d4',
    teal:    '#14b8a6',
    lime:    '#84cc16',
    orange:  '#f97316',
    amber:   '#f59e0b',
    rose:    '#f43f5e',
    pink:    '#ec4899',
    violet:  '#8b5cf6',
    fuchsia: '#d946ef',
    sky:     '#0ea5e9',
    indigo:  '#6366f1',
  };
  return hexColors[color] || '#9ca3af';
}
